import moment from 'moment';
import dayjs from 'dayjs';
// Helpers
import { ValueLabelOption } from './campaignEditModal';
import { TimeLineDeliverable } from './deliverables';
// Types
import {
  CampaignSmPlatform,
  CampaignTaskType,
  CbDueDateType,
  CbDisplayPeriod,
  CbFixedDateType,
  CampaignMediaType,
  CampaignExampleFileInput,
  CreateCampaignTaskV2Input,
  CreateCampaignContentItemInput,
  CbDeliverableV2Status,
} from 'api/graphql-global-types';
import {
  GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity_tasksV2,
  GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2,
  GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items,
} from 'api/campaignV2/types/GetCampaignOffers';

// Define the missing constant
const dateRangeSpecificTypes = [CbDueDateType.FIXED, CbDueDateType.RANGE];

// Type definition for content items that can come from various sources
export interface ContentItemBase {
  type: CampaignTaskType;
  title: string | null;
  description: string | null;
  mediaType?: CampaignMediaType | null;
  customCaption?: string | null;
}

// Union type for all possible content item inputs (GraphQL types + input types)
export type ContentItemInput =
  | (ContentItemBase & {
      // GraphQL types have additional fields that we want to exclude in transformation
      id?: string;
      orderIdx?: number | null;
      dueDate?: any;
      __typename?: string;
    })
  | CreateCampaignContentItemInput;

export const mapSocialMediaToLabel = (
  socialMediaOption?: CampaignSmPlatform
): string => {
  switch (socialMediaOption) {
    case CampaignSmPlatform.IG:
      return 'Instagram';
    case CampaignSmPlatform.TT:
      return 'TikTok';
    case CampaignSmPlatform.TW:
      return 'X';
    default:
      return 'Millions';
  }
};

export const sortDeliverablesChecklistByOrderIdx = (
  list: TimeLineDeliverable[]
): any => {
  if (!list) return [];

  return list?.slice()?.sort((a: any, b: any) => a.orderIdx - b.orderIdx);
};

export const sortDeliverablesChecklistByType = (
  list: TimeLineDeliverable[]
): any => {
  if (!list) return [];

  return list
    ?.slice()
    .sort((a: any, b: any) =>
      a.type === CampaignTaskType.CONTENT && b.type !== CampaignTaskType.CONTENT
        ? -1
        : a.type !== CampaignTaskType.CONTENT &&
          b.type === CampaignTaskType.CONTENT
        ? 1
        : 0
    );
};

export const getPostDueDateLabel = (
  editingItem:
    | GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2
    | GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity_tasksV2
    | null
): string => {
  if (!editingItem) return 'Not set';
  const item = editingItem as any;

  // Check if this is a local item with flat structure
  const isLocalItem = 'postDueDateType' in item || 'contentDueDateType' in item;

  let dueDate;
  if (isLocalItem) {
    dueDate = {
      type: item.postDueDateType || item.contentDueDateType,
      fixedDate: item.postFixedDate || item.contentFixedDate,
      fixedType: item.postFixedDateType || item.contentFixedDateType,
      intervalDays: item.postValueInterval || item.contentValueInterval,
      rangeFrom: item.postRangeFrom || item.contentRangeFrom,
      rangeTo: item.postRangeTo || item.contentRangeTo,
    };
  } else {
    dueDate = item.dueDate;
  }

  if (!dueDate) return 'Due date not set';

  switch (dueDate.type) {
    case CbDueDateType.CONTENT_BASED:
      if (dueDate.intervalDays !== undefined) {
        let displayValue = dueDate.intervalDays;
        let displayPeriodLabel = 'days';

        // For local items, check if we need to convert from weeks to days
        if (isLocalItem && item.postDisplayPeriod === CbDisplayPeriod.WEEKS) {
          // postValueInterval is already in weeks, so use it directly
          displayValue = item.postValueInterval;
          displayPeriodLabel = displayValue === 1 ? 'week' : 'weeks';
        } else {
          // For GraphQL items or when postDisplayPeriod is DAYS, convert days to weeks if needed
          if (dueDate.intervalDays % 7 === 0) {
            displayValue = dueDate.intervalDays / 7;
            displayPeriodLabel = displayValue === 1 ? 'week' : 'weeks';
          } else {
            displayPeriodLabel = dueDate.intervalDays === 1 ? 'day' : 'days';
          }
        }

        return `${displayValue} ${displayPeriodLabel} after content approval`;
      }
      return 'After content approval';

    case CbDueDateType.FIXED: {
      if (dueDate.fixedDate) {
        const dateLabel = dayjs(dueDate.fixedDate).format('MMMM D, YYYY');

        if (dueDate.fixedType === CbFixedDateType.BEFORE) {
          return `Before ${dateLabel}`;
        } else if (dueDate.fixedType === CbFixedDateType.EXACT) {
          return `On ${dateLabel}`;
        }
        // fixedType is optional (campaign-builder saves fixed dates without
        // one), so a set date must still render.
        return dateLabel;
      }
      return 'Specific date not set';
    }

    case CbDueDateType.RANGE: {
      if (dueDate.rangeFrom && dueDate.rangeTo) {
        const fromDate = dayjs(dueDate.rangeFrom);
        const toDate = dayjs(dueDate.rangeTo);

        return `${fromDate.format('MMMM D')} - ${toDate.format(
          'MMMM D, YYYY'
        )}`;
      }
      return 'Date range not set';
    }

    case CbDueDateType.APPLICATION_BASED:
      return 'After application approval';

    default:
      return 'Due date not set';
  }
};

export const getContentDueDateLabel = (
  editingItem:
    | GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2
    | GetCampaignsV2_getCampaigns_entities_activities_UgcActivity_tasks
    | GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity_tasksV2
    | null
): string => {
  if (!editingItem) return 'Not set';

  // Find the first content item with a due date
  const contentItems =
    'contentItems' in editingItem ? editingItem.contentItems : [];

  const firstContentItem =
    contentItems?.find(
      (contentItem: any) =>
        contentItem.__typename === 'CampaignContentTask' && contentItem.dueDate
    ) ?? editingItem;

  if (
    !firstContentItem ||
    firstContentItem.__typename !== 'CampaignContentTask' ||
    !('dueDate' in firstContentItem) ||
    !firstContentItem.dueDate
  ) {
    return 'Content due date not set';
  }

  const dueDate = firstContentItem.dueDate;

  switch (dueDate.type) {
    case CbDueDateType.APPLICATION_BASED:
      if (dueDate.intervalDays !== undefined) {
        let displayValue = dueDate.intervalDays;
        let displayPeriodLabel = 'days';

        if (dueDate.intervalDays && dueDate.intervalDays % 7 === 0) {
          displayValue = dueDate.intervalDays / 7;
          displayPeriodLabel = displayValue === 1 ? 'week' : 'weeks';
        } else {
          displayPeriodLabel =
            dueDate.intervalDays && dueDate.intervalDays === 1 ? 'day' : 'days';
        }

        return `${displayValue} ${displayPeriodLabel} after application approval`;
      }
      return 'After application approval';

    case CbDueDateType.FIXED: {
      if (dueDate.fixedDate) {
        const dateLabel = dayjs(dueDate.fixedDate).format('MMMM D, YYYY');

        if (dueDate.fixedType === CbFixedDateType.BEFORE) {
          return `Before ${dateLabel}`;
        } else if (dueDate.fixedType === CbFixedDateType.EXACT) {
          return `On ${dateLabel}`;
        }
        // fixedType is optional (campaign-builder saves fixed dates without
        // one), so a set date must still render.
        return dateLabel;
      }
      return 'Specific date not set';
    }

    case CbDueDateType.RANGE: {
      if (dueDate.rangeFrom && dueDate.rangeTo) {
        const fromDate = dayjs(dueDate.rangeFrom);
        const toDate = dayjs(dueDate.rangeTo);

        return `${fromDate.format('MMMM D')} - ${toDate.format(
          'MMMM D, YYYY'
        )}`;
      }
      return 'Date range not set';
    }

    default:
      return 'Content due date not set';
  }
};

export type ContentPiece = {
  mediaType: CampaignMediaType;
  title: string;
  description: string;
};

export type TimelineDeliverableExample = CampaignExampleFileInput & {
  url?: string | null;
  name?: string | null;
};

export type TimelineDeliverableFormData = {
  contentAmount: number;
  contentPieces: ContentPiece[];
  examples: (TimelineDeliverableExample | File)[];
  contentSetup: {
    dueDateType: CbDueDateType;
    dueDate?: Date;
    approvalNumber?: number;
    approvalUnit?: CbDisplayPeriod;
  };
  postDetails: {
    postType: CampaignTaskType;
    postTitle?: string;
    description?: string;
    suggestedCaption?: string;
    hashtags?: string;
    mentions?: string;
    dueDateType?: CbDueDateType | 'specific';
    dueDate?: Date;
    approvalNumber?: number;
    approvalUnit?: CbDisplayPeriod;
    dateRangeType?: CbDueDateType.FIXED | CbDueDateType.RANGE;
    singleDateType?: CbFixedDateType | null;
    startDate?: Date;
    endDate?: Date;
  };
};

export const emptyFormData: Partial<TimelineDeliverableFormData> = {
  contentAmount: 1,
  contentPieces: [
    { mediaType: CampaignMediaType.VIDEO, title: '', description: '' },
  ],
  examples: [],
  contentSetup: {
    dueDateType: '' as CbDueDateType,
  },
  postDetails: {
    postType: '' as CampaignTaskType,
    postTitle: '',
    description: '',
    suggestedCaption: '',
    hashtags: '',
    mentions: '',
    dueDateType: '' as CbDueDateType,
  },
};

export const generateDefaultFormData = (
  platform: CampaignSmPlatform
): Partial<TimelineDeliverableFormData> => {
  return {
    ...emptyFormData,
    postDetails: {
      ...emptyFormData.postDetails,
      postType: getPostTaskOptions(platform)[0].value as CampaignTaskType,
      dueDateType: 'specific' as const,
      dateRangeType: CbDueDateType.FIXED,
      singleDateType: CbFixedDateType.EXACT,
    },
  };
};

const convertDaysToWeeks = (intervalDays: number | null | undefined) => {
  if (!intervalDays) return { number: undefined, unit: undefined };

  if (intervalDays % 7 === 0) {
    return {
      number: intervalDays / 7,
      unit: CbDisplayPeriod.WEEKS,
    };
  }

  return {
    number: intervalDays,
    unit: CbDisplayPeriod.DAYS,
  };
};

const convertWeeksToDays = (
  number: number | undefined,
  unit: CbDisplayPeriod | undefined
) => {
  if (!number || !unit) return undefined;

  if (unit === CbDisplayPeriod.WEEKS) {
    return number * 7;
  }

  return number; // if not weeks, then it's already in days
};

export const mapEditingItemToFormData = (
  editingItem: GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2 | null
): Partial<TimelineDeliverableFormData> => {
  if (!editingItem) return emptyFormData;

  const item = editingItem as any;

  // Check if this is a flat structure (like CreateCampaignTaskV2Input)
  const isFlatStructure =
    'postDueDateType' in item || 'contentDueDateType' in item;

  let contentDueDate = null;
  let dueDate = null;
  let contentApproval: {
    number: number | undefined;
    unit: CbDisplayPeriod | undefined;
  } = { number: undefined, unit: undefined };
  let postApproval: {
    number: number | undefined;
    unit: CbDisplayPeriod | undefined;
  } = { number: undefined, unit: undefined };
  let effectiveDueDateType = null;

  if (isFlatStructure) {
    // Handle flat structure (CreateCampaignTaskV2Input format)
    if (item.contentDueDateType) {
      if (
        item.contentDueDateType === CbDueDateType.FIXED &&
        item.contentFixedDate
      ) {
        // Fixed date
        contentDueDate = {
          type: item.contentDueDateType,
          fixedDate: item.contentFixedDate,
        };
      } else if (
        item.contentDueDateType === CbDueDateType.APPLICATION_BASED &&
        item.contentValueInterval &&
        item.contentCbDisplayPeriod
      ) {
        // Approval-based date
        contentDueDate = {
          type: item.contentDueDateType,
          intervalDays: item.contentValueInterval,
        };

        // Convert days back to weeks for display if needed
        if (item.contentCbDisplayPeriod === CbDisplayPeriod.DAYS) {
          contentApproval = convertDaysToWeeks(item.contentValueInterval);
        } else {
          contentApproval = {
            number: item.contentValueInterval,
            unit: item.contentCbDisplayPeriod,
          };
        }
      }
    }

    // Handle post due date
    if (item.postDueDateType) {
      if (
        (item.postDueDateType === CbDueDateType.FIXED ||
          item.postDueDateType === CbDueDateType.RANGE) &&
        (item.postFixedDate || item.postRangeFrom)
      ) {
        // Fixed date or range
        dueDate = {
          type: item.postDueDateType,
          fixedDate: item.postFixedDate,
          fixedType: item.postFixedDateType,
          rangeFrom: item.postRangeFrom,
          rangeTo: item.postRangeTo,
        };
      } else if (
        (item.postDueDateType === CbDueDateType.CONTENT_BASED ||
          item.postDueDateType === CbDueDateType.APPLICATION_BASED) &&
        item.postValueInterval &&
        item.postDisplayPeriod
      ) {
        // Approval-based date
        dueDate = {
          type: item.postDueDateType,
          intervalDays: item.postValueInterval,
        };

        // Convert days back to weeks for display if needed
        if (item.postDisplayPeriod === CbDisplayPeriod.DAYS) {
          postApproval = convertDaysToWeeks(item.postValueInterval);
        } else {
          postApproval = {
            number: item.postValueInterval,
            unit: item.postDisplayPeriod,
          };
        }
      }

      effectiveDueDateType = item.postDueDateType;
    }
  } else {
    // Handle nested structure (GraphQL format)
    dueDate = item.dueDate;

    // For content due date, check if this is a content task first
    if (item.__typename === 'CampaignContentTask' && item.dueDate) {
      contentDueDate = item.dueDate;
    } else {
      // Otherwise, look in content items
      const firstContentItem = editingItem.contentItems?.find(
        (contentItem: ContentItemInput) =>
          '__typename' in contentItem &&
          contentItem.__typename === 'CampaignContentTask' &&
          'dueDate' in contentItem &&
          contentItem.dueDate
      );

      contentDueDate =
        firstContentItem && 'dueDate' in firstContentItem
          ? firstContentItem.dueDate
          : null;
    }

    contentApproval = contentDueDate?.intervalDays
      ? convertDaysToWeeks(contentDueDate.intervalDays)
      : { number: undefined, unit: undefined };

    postApproval = dueDate?.intervalDays
      ? convertDaysToWeeks(dueDate.intervalDays)
      : { number: undefined, unit: undefined };

    effectiveDueDateType = dueDate?.type;
  }

  // Filter out FINAL_CONTENT items - they are auto-generated by the backend
  const regularContentItems = (editingItem.contentItems ?? []).filter(
    (item: ContentItemInput) => item.type !== CampaignTaskType.FINAL_CONTENT
  );

  return {
    contentAmount: regularContentItems.length || 1,
    contentPieces:
      regularContentItems.map((contentItem: ContentItemInput) => ({
        mediaType: contentItem.mediaType ?? CampaignMediaType.VIDEO,
        title: contentItem.title ?? '',
        description: contentItem.description ?? '',
      })) ?? emptyFormData.contentPieces,
    examples: item.examples ?? [],
    contentSetup: {
      dueDateType: contentDueDate?.type ?? CbDueDateType.APPLICATION_BASED,
      dueDate: contentDueDate?.fixedDate
        ? dayjs(contentDueDate.fixedDate).toDate()
        : undefined,
      approvalNumber: contentApproval.number,
      approvalUnit: contentApproval.unit,
    },
    postDetails: {
      postType: editingItem.type,
      postTitle: editingItem.title ?? emptyFormData.postDetails?.postTitle,
      description:
        editingItem.description ?? emptyFormData.postDetails?.description,
      suggestedCaption:
        item.suggestedCaption ?? emptyFormData.postDetails?.suggestedCaption,
      hashtags: (item.hashtags ?? []).join(' '),
      mentions: (item.mentions ?? []).join(' '),
      dueDateType: dateRangeSpecificTypes.includes(effectiveDueDateType)
        ? 'specific'
        : effectiveDueDateType ?? emptyFormData.postDetails?.dueDateType,
      dueDate: dueDate?.fixedDate
        ? dayjs(dueDate.fixedDate).toDate()
        : undefined,
      singleDateType: dueDate?.fixedType,
      startDate: dueDate?.rangeFrom
        ? dayjs(dueDate.rangeFrom).toDate()
        : undefined,
      endDate: dueDate?.rangeTo ? dayjs(dueDate.rangeTo).toDate() : undefined,
      approvalNumber: postApproval.number,
      approvalUnit: postApproval.unit,
      dateRangeType: dateRangeSpecificTypes.includes(effectiveDueDateType)
        ? effectiveDueDateType
        : undefined,
    },
  };
};

export const mapFormDataToCreateCampaignTaskV2Input = (
  data: TimelineDeliverableFormData
): CreateCampaignTaskV2Input => {
  const contentItems = (data.contentPieces || []).map((piece) => ({
    mediaType: piece.mediaType,
    title: piece.title,
    description: piece.description,
    type: CampaignTaskType.CONTENT,
  }));

  const postData = {
    type: data.postDetails.postType,
    title: data.postDetails.postTitle,
    description: data.postDetails.description,
    suggestedCaption: data.postDetails.suggestedCaption,
    hashtags: parsePostTokens(data.postDetails.hashtags),
    mentions: parsePostTokens(data.postDetails.mentions),
    postDueDateType:
      data.postDetails.dueDateType === 'specific'
        ? data.postDetails.dateRangeType
        : data.postDetails.dueDateType,
    postFixedDateType:
      data.postDetails.dateRangeType === CbDueDateType.FIXED
        ? data.postDetails.singleDateType ?? undefined
        : undefined,
    postFixedDate:
      data.postDetails.dateRangeType === CbDueDateType.FIXED &&
      data.postDetails.dueDate
        ? dayjs(data.postDetails.dueDate).format('YYYY-MM-DD')
        : undefined,
    postRangeFrom:
      data.postDetails.dateRangeType === CbDueDateType.RANGE &&
      data.postDetails.startDate
        ? dayjs(data.postDetails.startDate).format('YYYY-MM-DD')
        : undefined,
    postRangeTo:
      data.postDetails.dateRangeType === CbDueDateType.RANGE &&
      data.postDetails.endDate
        ? dayjs(data.postDetails.endDate).format('YYYY-MM-DD')
        : undefined,
    postDisplayPeriod:
      data.postDetails.dueDateType === CbDueDateType.CONTENT_BASED ||
      data.postDetails.dueDateType === CbDueDateType.APPLICATION_BASED
        ? data.postDetails.approvalUnit
        : undefined,
    postValueInterval:
      data.postDetails.dueDateType === CbDueDateType.CONTENT_BASED ||
      data.postDetails.dueDateType === CbDueDateType.APPLICATION_BASED
        ? data.postDetails.approvalNumber
        : undefined,
  };

  const contentData = {
    contentDueDateType: data.contentSetup?.dueDateType,
    contentFixedDate:
      data.contentSetup?.dueDateType === CbDueDateType.FIXED &&
      data.contentSetup?.dueDate
        ? dayjs(data.contentSetup.dueDate).format('YYYY-MM-DD')
        : undefined,
    contentValueInterval:
      data.contentSetup?.dueDateType === CbDueDateType.APPLICATION_BASED
        ? data.contentSetup?.approvalNumber
        : undefined,
    contentCbDisplayPeriod:
      data.contentSetup?.dueDateType === CbDueDateType.APPLICATION_BASED
        ? data.contentSetup?.approvalUnit
        : undefined,
  };

  const result = {
    ...postData,
    ...contentData,
    contentItems,
  };

  return result;
};

const parsePostTokens = (value?: string): string[] =>
  (value ?? '')
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);

export const convertToMoment = (
  date: Date | undefined
): moment.Moment | undefined => {
  return date ? moment(date) : undefined;
};

export const convertToDate = (
  momentObj: moment.Moment | null
): Date | undefined => {
  return momentObj ? momentObj.toDate() : undefined;
};

export const getTitlePlaceholder = (platform: CampaignSmPlatform): string => {
  if (platform === CampaignSmPlatform.IG) {
    return 'Example: Post An In-Feed Instagram Reel';
  }
  return 'Example: Post A TikTok Video';
};

export const getDueDateDisplayValue = (
  value: CbDueDateType | 'specific' | undefined
): CbDueDateType | 'specific' | undefined => {
  if (value === CbDueDateType.CONTENT_BASED) {
    return CbDueDateType.CONTENT_BASED;
  }
  if (value === CbDueDateType.FIXED || value === CbDueDateType.RANGE) {
    return 'specific';
  }
  return value;
};

export const getDueDateOptions = (): ValueLabelOption[] => [
  {
    label: 'Content Approval-Based Timeline',
    value: CbDueDateType.CONTENT_BASED,
  },
  { label: 'Specific Date or Date Range', value: 'specific' },
];

export const getApprovalUnitOptions = (): ValueLabelOption[] => [
  { label: 'Days', value: CbDisplayPeriod.DAYS },
  { label: 'Weeks', value: CbDisplayPeriod.WEEKS },
];

export const getSingleDateTypeOptions = (): ValueLabelOption[] => [
  { label: 'Before', value: CbFixedDateType.BEFORE },
  { label: 'On', value: CbFixedDateType.EXACT },
];

export const getPostTaskOptions = (
  platform: CampaignSmPlatform
): ValueLabelOption[] => {
  const isTikTok = platform === CampaignSmPlatform.TT;
  return isTikTok
    ? [{ label: 'TikTok Video', value: CampaignTaskType.TT_VIDEO }]
    : [
        { label: 'Live', value: CampaignTaskType.IG_LIVE },
        { label: 'Post', value: CampaignTaskType.IG_POST },
        { label: 'Reel', value: CampaignTaskType.IG_REEL },
        { label: 'Story', value: CampaignTaskType.IG_STORY },
      ];
};

export const getContentDueDateTypeOptions = (): ValueLabelOption[] => [
  { value: CbDueDateType.FIXED, label: 'Fixed Date' },
  { value: CbDueDateType.APPLICATION_BASED, label: 'Application Based' },
];

export const createContentPiece = (
  index: number,
  existingPiece?: Partial<ContentPiece>
): ContentPiece => {
  return {
    mediaType: existingPiece?.mediaType ?? CampaignMediaType.VIDEO,
    title: existingPiece?.title ?? '',
    description: existingPiece?.description ?? '',
    ...existingPiece,
  };
};

export const generateContentPieces = (
  amount: number,
  existingPieces: Partial<ContentPiece>[] = []
): ContentPiece[] => {
  return Array.from({ length: amount || 1 }, (_, index) =>
    createContentPiece(index, existingPieces[index])
  );
};

export const getDisabledDate = (current: moment.Moment | null): boolean => {
  return (
    (current && current < moment().add(2, 'weeks').startOf('day')) || false
  );
};

export const createNumberOptions = (max: number): ValueLabelOption[] => {
  return Array.from({ length: max }, (_, index) => ({
    label: (index + 1).toString(),
    value: (index + 1).toString(),
  }));
};

export const findNumberOption = (
  options: ValueLabelOption[],
  value: string | number | null | undefined
): ValueLabelOption | null => {
  if (value === null || value === undefined) return null;
  return options.find((opt) => opt.value === value.toString()) ?? null;
};

export const validateStep1 = (
  formData: TimelineDeliverableFormData
): boolean => {
  const { contentAmount, contentPieces, contentSetup } = formData;

  if (!contentAmount || contentAmount < 1) {
    return false;
  }
  if (!contentPieces || contentPieces.length === 0) {
    return false;
  }
  for (const piece of contentPieces) {
    if (
      !piece.mediaType ||
      !piece.title?.trim() ||
      !piece.description?.trim()
    ) {
      return false;
    }
  }
  if (!contentSetup?.dueDateType) {
    return false;
  }
  if (
    contentSetup.dueDateType === CbDueDateType.FIXED &&
    !contentSetup.dueDate
  ) {
    return false;
  }
  if (contentSetup.dueDateType === CbDueDateType.APPLICATION_BASED) {
    if (!contentSetup.approvalUnit || !contentSetup.approvalNumber) {
      return false;
    }
  }

  return true;
};

export const validateStep2 = (
  formData: TimelineDeliverableFormData
): boolean => {
  const { postDetails } = formData;
  if (!postDetails?.postType) {
    return false;
  }
  if (!postDetails.postTitle?.trim()) {
    return false;
  }
  if (!postDetails.description?.trim()) {
    return false;
  }
  if (!postDetails.dueDateType) {
    return false;
  }
  if (postDetails.dueDateType === CbDueDateType.CONTENT_BASED) {
    if (!postDetails.approvalUnit || !postDetails.approvalNumber) {
      return false;
    }
  }
  if (postDetails.dueDateType === 'specific') {
    if (!postDetails.dateRangeType) {
      return false;
    }
    if (postDetails.dateRangeType === CbDueDateType.FIXED) {
      if (!postDetails.singleDateType || !postDetails.dueDate) {
        return false;
      }
    }
    if (postDetails.dateRangeType === CbDueDateType.RANGE) {
      if (!postDetails.startDate || !postDetails.endDate) {
        return false;
      }
    }
  }

  return true;
};

export const transformTasksV2ForUpdate = (
  tasks: TimeLineDeliverable[]
): CreateCampaignTaskV2Input[] => {
  // Filter out FINAL_CONTENT tasks - they are auto-generated by the backend
  const filteredTasks = tasks.filter(
    (task: any) => task.type !== CampaignTaskType.FINAL_CONTENT
  );
  return filteredTasks.map((task) => {
    // Check if this is a create input task by looking for the specific properties that indicate it's from the form rather than GraphQL
    const isCreateInput =
      'postDueDateType' in task || 'contentDueDateType' in task;

    if (isCreateInput) {
      const createInputTask = task as CreateCampaignTaskV2Input;

      // For create input tasks, we need to convert the form data to the correct format
      let contentValueInterval = createInputTask.contentValueInterval;
      let contentCbDisplayPeriod = createInputTask.contentCbDisplayPeriod;
      let postValueInterval = createInputTask.postValueInterval;
      let postDisplayPeriod = createInputTask.postDisplayPeriod;

      if (
        createInputTask.contentValueInterval &&
        createInputTask.contentCbDisplayPeriod
      ) {
        if (createInputTask.contentCbDisplayPeriod === CbDisplayPeriod.DAYS) {
          // If it's in days, check if we should convert to weeks
          if (createInputTask.contentValueInterval > 6) {
            const weeks = convertDaysToWeeks(
              createInputTask.contentValueInterval
            );
            contentValueInterval = weeks.number;
            contentCbDisplayPeriod = weeks.unit;
          }
          // If <= 6 days, keep as days
        } else if (
          createInputTask.contentCbDisplayPeriod === CbDisplayPeriod.WEEKS
        ) {
          // If it's weeks, convert to days first to check
          const days = convertWeeksToDays(
            createInputTask.contentValueInterval,
            createInputTask.contentCbDisplayPeriod
          );
          if (days && days > 6) {
            // If more than 6 days, keep as weeks
            contentValueInterval = createInputTask.contentValueInterval;
            contentCbDisplayPeriod = CbDisplayPeriod.WEEKS;
          } else {
            // If 6 days or less, convert to days
            contentValueInterval = days;
            contentCbDisplayPeriod = CbDisplayPeriod.DAYS;
          }
        }
      }

      if (
        createInputTask.postValueInterval &&
        createInputTask.postDisplayPeriod
      ) {
        if (createInputTask.postDisplayPeriod === CbDisplayPeriod.DAYS) {
          if (createInputTask.postValueInterval > 6) {
            const weeks = convertDaysToWeeks(createInputTask.postValueInterval);
            postValueInterval = weeks.number;
            postDisplayPeriod = weeks.unit;
          }
        } else if (
          createInputTask.postDisplayPeriod === CbDisplayPeriod.WEEKS
        ) {
          const days = convertWeeksToDays(
            createInputTask.postValueInterval,
            createInputTask.postDisplayPeriod
          );
          if (days && days > 6) {
            postValueInterval = createInputTask.postValueInterval;
            postDisplayPeriod = CbDisplayPeriod.WEEKS;
          } else {
            postValueInterval = days;
            postDisplayPeriod = CbDisplayPeriod.DAYS;
          }
        }
      }

      const result = {
        ...createInputTask,
        contentValueInterval,
        contentCbDisplayPeriod,
        postValueInterval,
        postDisplayPeriod,
      };

      return result;
    }

    // For GraphQL tasks, we also need to apply the same conversion logic because they might have values that need to be converted
    const graphqlTask =
      task as GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2;

    const customBriefTask =
      task as GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity_tasksV2;

    const firstContentItem = graphqlTask.contentItems?.find(
      (contentItem: any) =>
        contentItem.__typename === 'CampaignContentTask' && contentItem.dueDate
    );

    const contentDueDate =
      firstContentItem && firstContentItem.__typename === 'CampaignContentTask'
        ? (firstContentItem as { dueDate?: any }).dueDate
        : undefined;

    const contentDueDateType =
      contentDueDate?.type || CbDueDateType.APPLICATION_BASED;

    const postDueDate = 'dueDate' in graphqlTask ? graphqlTask.dueDate : null;
    const postDueDateType = postDueDate?.type;

    // Apply conversion logic to GraphQL tasks as well
    let contentValueInterval = contentDueDate?.intervalDays;
    let contentCbDisplayPeriod = CbDisplayPeriod.DAYS;
    let postValueInterval = postDueDate?.intervalDays;
    let postDisplayPeriod = CbDisplayPeriod.DAYS;

    // Convert content values if needed
    if (contentValueInterval && contentValueInterval > 6) {
      const weeks = convertDaysToWeeks(contentValueInterval);
      contentValueInterval = weeks.number;
      contentCbDisplayPeriod = weeks.unit || CbDisplayPeriod.DAYS;
    }

    // Convert post values if needed
    if (postValueInterval && postValueInterval > 6) {
      const weeks = convertDaysToWeeks(postValueInterval);
      postValueInterval = weeks.number;
      postDisplayPeriod = weeks.unit || CbDisplayPeriod.DAYS;
    }

    const transformedTask: CreateCampaignTaskV2Input = {
      type: graphqlTask.type,
      title: graphqlTask.title,
      description: graphqlTask.description,
      suggestedCaption: graphqlTask.suggestedCaption || null,
      hashtags: customBriefTask.hashtags ?? undefined,
      mentions: customBriefTask.mentions ?? undefined,
      contentDueDateType: contentDueDateType,
      contentFixedDate:
        contentDueDateType === CbDueDateType.FIXED
          ? contentDueDate?.fixedDate
          : undefined,
      contentValueInterval:
        contentDueDateType === CbDueDateType.APPLICATION_BASED
          ? contentValueInterval
          : undefined,
      contentCbDisplayPeriod:
        contentDueDateType === CbDueDateType.APPLICATION_BASED
          ? contentCbDisplayPeriod
          : undefined,
      postDueDateType: postDueDateType,
      postFixedDate:
        postDueDateType === CbDueDateType.FIXED
          ? postDueDate?.fixedDate
          : undefined,
      postFixedDateType:
        postDueDateType === CbDueDateType.FIXED
          ? postDueDate?.fixedType
          : undefined,
      postRangeFrom:
        postDueDateType === CbDueDateType.RANGE
          ? postDueDate?.rangeFrom
          : undefined,
      postRangeTo:
        postDueDateType === CbDueDateType.RANGE
          ? postDueDate?.rangeTo
          : undefined,
      postValueInterval:
        postDueDateType === CbDueDateType.CONTENT_BASED ||
        postDueDateType === CbDueDateType.APPLICATION_BASED
          ? postValueInterval
          : undefined,
      postDisplayPeriod:
        postDueDateType === CbDueDateType.CONTENT_BASED ||
        postDueDateType === CbDueDateType.APPLICATION_BASED
          ? postDisplayPeriod
          : undefined,
    };

    // Transform content items if they exist (filter out FINAL_CONTENT items)
    if (graphqlTask.contentItems && graphqlTask.contentItems.length > 0) {
      const regularContentItems = graphqlTask.contentItems.filter(
        (contentItem: any) =>
          contentItem.type !== CampaignTaskType.FINAL_CONTENT
      );
      transformedTask.contentItems = regularContentItems.map(
        (contentItem: any): CreateCampaignContentItemInput => ({
          type: contentItem.type,
          title: contentItem.title || '',
          description: contentItem.description || '',
          mediaType: contentItem.mediaType || CampaignMediaType.VIDEO,
          customCaption: contentItem.customCaption || null,
        })
      );
    }

    return transformedTask;
  });
};

export const getStatusText = (status: CbDeliverableV2Status): string => {
  switch (status) {
    case CbDeliverableV2Status.NOT_SUBMITTED:
      return 'Not Submitted';
    case CbDeliverableV2Status.TALENT_ACTION_NEEDED:
      return 'Action Needed';
    case CbDeliverableV2Status.CONTENT_REJECTED:
      return 'Rejected';
    case CbDeliverableV2Status.CONTENT_APPROVED:
      return 'Approved';
    case CbDeliverableV2Status.ON_BRAND_REVIEW:
      return 'Under Review';
    case CbDeliverableV2Status.PUBLISHED:
      return 'Published';
    default:
      return 'Unknown';
  }
};

export const checkPublishUploadAllowed = (
  deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
): boolean => {
  return deliverable.status === CbDeliverableV2Status.CONTENT_APPROVED;
};

export const deliverableHasContentAction = (
  status: CbDeliverableV2Status
): boolean => {
  return [
    CbDeliverableV2Status.CONTENT_REJECTED,
    CbDeliverableV2Status.TALENT_ACTION_NEEDED,
    CbDeliverableV2Status.NOT_SUBMITTED,
  ].includes(status);
};

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm', '.avi', '.mkv'];
export const getDeliverableItemMedia = (
  item?: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
): { url: string; name: string; isVideo: boolean; originalUrl: string } => {
  let url = '';
  let name = '';
  let originalUrl = '';

  if (
    item?.__typename === 'CbDeliverableFilesItemV2' &&
    item.files?.length > 0
  ) {
    const firstFile = item.files[0];
    url = firstFile.url || '';
    name = firstFile.name || '';
    originalUrl = firstFile.originalUrl || '';
  } else if (item?.__typename === 'CbDeliverableFileItemV2') {
    url = item.url || '';
    name = item.name || '';
    originalUrl = item.originalUrl || '';
  }

  const isVideo = VIDEO_EXTENSIONS.some((ext) =>
    name.toLowerCase().endsWith(ext)
  );

  return { url, name, isVideo, originalUrl };
};
